import React, { useState, useRef, useMemo } from "react";
import { ContentHeader } from "@components";
import { useAuth } from "../contexts/AuthContext";
import DataTable from "../components/datatable/DataTable";
import UserModal from "../components/modal/UserModal";
import PermissionModal from "../components/modal/UserPermissionModal";
import API from "../api";
import toastr from "toastr";
import { useNavigate } from "react-router-dom";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3000";

function getPhotoUrl(photo: string | undefined | null): string | null {
  if (!photo) return null;
  if (
    photo.startsWith("http://") ||
    photo.startsWith("https://") ||
    photo.startsWith("blob:")
  ) {
    return photo;
  }
  return `${API_BASE}${photo.startsWith("/") ? "" : "/"}${photo}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface User {
  id?: number;
  user_type_id: string;
  prefix: string;
  first_name: string;
  last_name: string;
  username?: string;
  email: string;
  age: string;
  sex: string;
  account_no: string;
  aadhar_no: string;
  contact_no: string;
  profile_photo?: string | File;
  address: string;
  status?: string;
  password?: string;
  salary_percentage?: string;
  salary_duration_type?: string;
  joining_date?: string;
  relieve_date?: string;
  service_details?: string;
  // Permissions
  dashboard?: number;
  users?: number;
  adduser?: number;
  edituser?: number;
  deleteuser?: number;
  manage_user_permission?: number;
  patients?: number;
  addpatient?: number;
  editpatient?: number;
  deletepatient?: number;
  investigation?: number;
  doctor?: number;
  add_doctor?: number;
  edit_doctor?: number;
  delete_doctor?: number;
  billing?: number;
  report?: number;
  view_report?: number;
  delete_report?: number;
  payments?: number;
  finance?: number;
  income?: number;
  expense?: number;
  finance_report?: number;
  test_settings?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Users: React.FC = () => {
  const { user }    = useAuth();
  const permissions = user?.permissions || {};
  const navigate    = useNavigate();

  const [showModal, setShowModal]                     = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser]               = useState<User | null>(null);
  const [modalMode, setModalMode]                     = useState<"add" | "edit">("add");
  const tableRef = useRef<any>(null);

  // ── Fetch users ─────────────────────────────────────────────────────────────
  const fetchUsers = async (
    page: number,
    search: string,
    sortBy    = "first_name",
    sortOrder = "asc"
  ) => {
    try {
      const res = await API.get(
        `/users/getUsers?page=${page}&pageSize=25&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      return { data: res.data.data, total: res.data.total };
    } catch {
      toastr.error("Failed to fetch users");
      return { data: [], total: 0 };
    }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setModalMode("add");
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEdit = (u: User) => {
    setModalMode("edit");
    setSelectedUser(u);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await API.delete(`/users/deleteUser/${id}`);
      toastr.success("User deleted successfully");
      tableRef.current?.refetch();
    } catch {
      toastr.error("Failed to delete user");
    }
  };

  const openPermissionModal = async (row: User) => {
    try {
      const res = await API.get(`/users/getUser/${row.id}`);
      if (res.data.success) {
        const fullUser = { ...row, ...(res.data.data || res.data.user || {}) };
        setSelectedUser(fullUser);
        setShowPermissionModal(true);
      } else {
        toastr.error("Failed to load user permissions");
      }
    } catch {
      toastr.error("Error loading user permissions");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const closePermissionModal = () => {
    setShowPermissionModal(false);
    setSelectedUser(null);
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      { header: "ID",        accessorKey: "id" },
      { header: "User Type", accessorKey: "user_type_name" },
      { header: "Full Name", accessorKey: "full_name" },
      { header: "Email",     accessorKey: "email" },
      { header: "Phone",     accessorKey: "contact_no" },
      {
        header: "Photo",
        id: "profile_photo",
        cell: ({ row }: any) => {
          const url = getPhotoUrl(
            typeof row.original.profile_photo === "string"
              ? row.original.profile_photo
              : undefined
          );
          return url ? (
            <img
              src={url}
              alt="Profile"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Status",
        id: "status",
        cell: ({ row }: any) => (
          <span
            className={`badge ${
              row.original.status == 1 ? "bg-success" : "bg-secondary"
            }`}
          >
            {row.original.status == 1 ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }: any) => {
          const data: User = row.original;
          const authUserTypeId = Number(user?.user_type_id);
          const authUserId = Number(user?.id);
          const isOwnData = Number(data.id) === authUserId;
          const canManage = (authUserTypeId === 1 || authUserTypeId === 2) && !isOwnData;
          return (
            <div className="d-flex justify-content-center gap-1">
              {canManage && permissions.edituser && (
                <button
                  className="btn btn-light"
                  onClick={() => handleEdit(data)}
                  title="Edit User"
                >
                  <i className="fa fa-edit" />
                </button>
              )}
              {canManage && permissions.manage_user_permission && (
                <button
                  className="btn btn-light"
                  onClick={() => openPermissionModal(data)}
                  title="Manage Permissions"
                >
                  <i className="fa fa-flag" />
                </button>
              )}
              <button
                className="btn btn-light"
                onClick={() => navigate("/settings")}
                title="Organisation"
              >
                <i className="fa fa-building" />
              </button>
              {canManage && permissions.deleteuser && (
                <button
                  className="btn btn-light"
                  onClick={() => handleDelete(data.id!)}
                  title="Delete"
                >
                  <i className="fa fa-trash" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [permissions, navigate, user?.user_type_id, user?.id]
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  const authUserTypeId = Number(user?.user_type_id);
  const canAddUser = (authUserTypeId === 1 || authUserTypeId === 2) && permissions.adduser;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Users" />
        {canAddUser && (
          <button className="btn btn-light px-5" onClick={handleAdd}>
            <i className="fa fa-plus me-1" /> Add New
          </button>
        )}
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchUsers}
                queryKey={["users"]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* User Add/Edit Modal */}
      {showModal && (
        <UserModal
          show={showModal}
          onClose={handleModalClose}
          mode={modalMode}
          userData={selectedUser}
          onSuccess={() => {
            handleModalClose();
            tableRef.current?.refetch();
          }}
        />
      )}

      {/* User Permission Modal */}
      <PermissionModal
        show={showPermissionModal}
        onClose={closePermissionModal}
        userData={selectedUser || undefined}
        onSaved={() => {
          closePermissionModal();
          tableRef.current?.refetch();
        }}
      />
    </div>
  );
};

export default Users;